import { Injectable } from '@angular/core';

import { IColumnIdentifer, IRMSOffset } from '../types';

import { Api } from './api';

@Injectable({
    providedIn: 'root'
})

export class GisToolsApi {

    constructor(private readonly api: Api) { }

    districtProperties: string = 'id, districtNumber';
    countyProperties: string = 'id, countyCode, districtNumber';

    async getDistricts(id: number[]) {
        return await this.api.http2.graphqlRequest(`query GetDistricts($id: [Int]) {
                districts(id: $id){
                    ${this.districtProperties}
                }
            }`, { id }, 'GET', 'districts');
    }

    async getCounties(id: number[]) {
        return await this.api.http2.graphqlRequest(`query GetCounties($id: [Int]) {
                counties(id: $id){
                    ${this.countyProperties}
                }
            }`, { id }, 'GET', 'counties');
    }

    async getCountiesByDistricts(districtNumbers: string[]) {
        return await this.api.http2.graphqlRequest(`query GetCounties($districtNumbers: [String]) {
                counties(districtNumber: $districtNumbers){
                    ${this.countyProperties}
                }
            }`, { districtNumbers }, 'GET', 'counties');
    }

    async getAllDistricts(props) {
        return await this.api.http2.graphqlRequest(`query GetDistricts {
            districts {
                ${props}
            }
        }`, {}, 'GET', 'districts');
    }

    async getAllCounties(props) {
        return await this.api.http2.graphqlRequest(`query GetCounties {
            counties {
                ${props}
            }
        }`, {}, 'GET', 'counties');
    }

    async getAllMunicipalities(props) {
        return await this.api.http2.graphqlRequest(`query GetMunicipalities {
            municipalities {
                ${props}
            }
        }`, {}, 'GET', 'municipalities');
    }

    async getAllZipCodes(props) {
        return await this.api.http2.graphqlRequest(`query GetZipCodes {
            zipCodes {
                ${props}
            }
        }`, {}, 'GET', 'zipCodes');
    }
    async getAllSchoolDistricts(props) {
        return await this.api.http2.graphqlRequest(`query GetSchoolDistricts {
            schooldistricts {
                ${props}
            }
        }`, {}, 'GET', 'schooldistricts');
    }

    async getMunicipalitiesByCounty(countyCode: string, props: string) {
        return await this.api.http2.graphqlRequest(`query GetMunicipalitiesByCounty($countyCode: [String]) {
            municipalities(countyCode: $countyCode) {
                ${props}
            }
        }`, { countyCode }, 'GET', 'municipalities');
    }

    async getAllPlanningPartners(props) {
        return await this.api.http2.graphqlRequest(`query GetPlanningPartners {
            planningPartners {
                ${props}
            }
        }`, {}, 'GET', 'planningPartners');
    }

    async getAllHouseDistricts(props) {
        return await this.api.http2.graphqlRequest(`query GetHouseDistricts {
            pahousedistricts {
                ${props}
            }
        }`, {}, 'GET', 'pahousedistricts');
    }

    async getAllSenateDistricts(props) {
        return await this.api.http2.graphqlRequest(`query GetSenateDistricts {
            pasenatedistricts {
                ${props}
            }
        }`, {}, 'GET', 'pasenatedistricts');
    }

    async getAllCongressionalDistricts(props) {
        return await this.api.http2.graphqlRequest(`query GetCongressionalDistricts {
            uscongressionaldistricts {
                ${props}
            }
        }`, {}, 'GET', 'uscongressionaldistricts');
    }

    async getAllStateRoutes(props) {
        return await this.api.http2.graphqlRequest(`query GetStateRoutes {
            stateRoutes {
                ${props}
            }
        }`, {}, 'GET', 'stateRoutes');
    }

    async getStateRoutesByCounty(countyCode, props) {
        return await this.api.http2.graphqlRequest(`query GetStateRoutes($countyCode: [String]) {
            stateRoutes(countyCode: $countyCode) {
                ${props}
            }
        }`, { countyCode }, 'GET', 'stateRoutes');
    }

    async getStateRoutesByCountyJuris(countyCode, jurisValues, props) {
        const jurisValuesAsStrings = jurisValues.map(x => x.toString());
        return await this.api.http2.graphqlRequest(`query GetStateRoutes($countyCode: [String], $jurisValuesAsStrings: [String]) {
            stateRoutes(countyCode: $countyCode, juris: $jurisValuesAsStrings) {
                ${props}
            }
        }`, { countyCode, jurisValuesAsStrings }, 'GET', 'stateRoutes');
    }

    async getAllSegments(props) {
        return await this.api.http2.graphqlRequest(`query GetSegments {
            rmssegments {
                ${props}
            }
        }`, {}, 'GET', 'rmssegments');
    }

    async getSegmentsByCountyAndRoute(countyCode, routeNumber, props) {
        return await this.api.http2.graphqlRequest(`query GetSegments($countyCode: [String], $routeNumber: [String]) {
            rmssegments(countyCode: $countyCode, stateRouteNumber: $routeNumber) {
                ${props}
            }
        }`, { countyCode, routeNumber }, 'GET', 'rmssegments');
    }

    async getSegmentsNearLatLong(latitude, longitude, tolerance) {
        return await this.api.http2.graphqlRequest(`query GetSegments($latitude: [Float], $longitude: [Float], $tolerance: [Float]) {
            rmssegments(latitude: $latitude, longitude: $longitude, buffer: $tolerance, orderBy: ["countyCode", "stateRouteNumber", "segmentNumber"]) {
                countyCode,
                stateRouteNumber,
                segmentNumber
            }
        }`, { latitude, longitude, tolerance }, 'GET', 'rmssegments');
    }

    async getSegmentsClosestToLatLong(latitude, longitude, tolerance, juris = ['1', '5']) {
        return await this.api.http2.graphqlRequest(`query GetSegments($latitude: [Float], $longitude: [Float], $juris: [String], $tolerance: [Float]) {
            rmssegments(latitude: $latitude, longitude: $longitude, juris: $juris, buffer: $tolerance, orderBy: ["distanceFromLatitudeLongitude"]) {
                countyCode,
                stateRouteNumber,
                segmentNumber,
                distanceFromLatitudeLongitude
            }
        }`, { latitude, longitude, juris, tolerance }, 'GET', 'rmssegments');
    }

    async getInterstatesByNetworkIndicator(interstateNetworkIndicator: string[]) {
        return await this.api.http2.graphqlRequest(`query GetSegments($interstateNetworkIndicator: [String]) {
            rmssegments(interstateNetworkIndicator: $interstateNetworkIndicator) {
                stateRouteNumber
            }
        }`, { interstateNetworkIndicator }, 'GET', 'rmssegments');
    }

    async getAllColumnIdentifiers(props) {
        return await this.api.http2.graphqlRequest<IColumnIdentifer[]>(`query GetColumnIdentifiers {
            columnIdentifiers {
                ${props}
            }
        }`, {}, 'GET', 'columnIdentifiers');
    }

    async getColumns(layerId, props) {
        return await this.api.http2.graphqlRequest(`query GetColumns($layerId: [Int]) {
            layers(id: $layerId) {
                columns {
                    ${props}
                }
            }
        }`, { layerId }, 'GET', 'layers');
    }

    async getOffsetForLatLong(lat, long, buffer, props) {
        return await this.api.http2.graphqlRequest(`query GetOffsetForLatLong($lat: [Float], $long: [Float], $buffer: [Float]) {
            rmsOffset(latitude: $lat, longitude: $long, buffer: $buffer) {
                ${props}
            }
        }`, { lat, long, buffer }, 'GET', 'rmsOffset');
    }

    async getOffsetForCRSLatLong(lat: number, long: number, county: string, route: string, buffer: number, props: string) {
        return await this.api.http2.graphqlRequest<IRMSOffset[]>(`query GetOffsetForLatLong($lat: [Float], $long: [Float], $county: [String], $route: [String], $buffer: [Float]) {
            rmsOffset(latitude: $lat, longitude: $long, countyCode: $county, stateRouteNumber: $route, buffer: $buffer) {
                ${props}
            }
        }`, { lat, long, county, route, buffer }, 'GET', 'rmsOffset');
    }

    async getNearbyLocalIntersections(lat, long, buffer, props) {
        return await this.api.http2.graphqlRequest(`query getNearbyLocalIntersections($lat: [Float], $long: [Float], $buffer: [Float]) {
            rmsintersections(latitude: $lat, longitude: $long, buffer: $buffer) {
                ${props}
            }
        }`, { lat, long, buffer }, 'GET', 'rmsintersections');
    }

    async getLatLongForOffset(cty, rte, seg, offset) {
        if (!offset) {
            offset = '0000';
        }
        return await this.api.http2.graphqlRequest<{ latLong: { latitude: number; longitude: number }}[]>(`query GetLatLongForOffset($cty: [String], $rte: [String], $seg: [String], $offset: [String]) {
            rmsOffset(countyCode: $cty, stateRouteNumber: $rte, segmentNumber: $seg, offset: $offset) {
                latLong {
                    latitude
                    longitude
                }
            }
        }`, { cty, rte, seg, offset }, 'GET', 'rmsOffset');
    }

    async getVideoLogForSegment(cty, rte, seg, year) {
        return await this.api.http2.graphqlRequest(`query GetVideoLogForSegment($cty: [String], $rte: [String], $seg: [String], $year:[String]) {
            videoLog(countyCode: $cty, stateRouteNumber: $rte, segmentNumber: $seg, year: $year) {
                countyCode
                countyName
                stateRouteNumber
                routeName
                direction
                sideIndicator
                juris
                segmentNumber
                streetName
                oppositeSegmentNumber
                previousCountyCode
                previousSegmentNumber
                nextSegmentNumber
                segmentLength
                nextCountyCode
                year
                yearRange
                rootFolder
                subFolders
                additionalFolders {
                    rootFolder
                    subFolders
                }
                isHighDefinition
                images {
                imageName1
                imageName2
                imageName3
                imageDate
                latitude
                longitude
                offset
                fileName
                sequenceNumber
                }
            }
        }`, { cty, rte, seg, year }, 'GET', 'videoLog');
    }

    async getOppositeOffset(cty, rte, juris, seg, offset) {
        return await this.api.http2.graphqlRequest(`query GetOppositeOffset($cty: [String], $rte: [String], $juris: [String], $seg: [String], $offset: [String]) {
            oppositeOffset (countyCode: $cty, stateRouteNumber: $rte, juris: $juris, segmentNumber: $seg, offset: $offset) {
                countyCode
                stateRouteNumber
                juris
                segmentNumber
                offset
            }
        }`, { cty, rte, juris, seg, offset }, 'GET', 'oppositeOffset');
    }

    async getFeaturesByObjectId(pluralObjectName, props, objectIds: number[]) {
        return await this.api.http2.graphqlRequest(`query ${pluralObjectName} ($objectIds: [Int]) {
            ${pluralObjectName} (objectId: $objectIds) {
                ${props}
            }
        }`, { objectIds }, 'GET', `${pluralObjectName}`);
    }

    async getRailCrossing(crossingId, props) {
        return await this.api.http2.graphqlRequest(`query GetRailCrossing($crossingId: [String]) {
            railCrossing(crossingNumber: $crossingId) {
                ${props}
            }
        }`, { crossingId }, 'GET', 'railCrossing');
    }
}
